import { useRef, useState } from 'react';
import { Button, Input, Alert } from '@bbpos/ui';
import { cn } from '@/constants/design-tokens';
import { getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { paiseToRupeeInput } from '@/utils/price-input';
import type { CurrencyCode } from '@bbpos/types';
import type { VariantAttribute } from '@/types/product';
import {
  type VariantRow,
  type AttributeDef,
  usableAttributes,
  generateCombinations,
  buildRowsFromCombinations,
  buildVariantAttributes,
  findLibraryAttribute,
  libraryValuesFor,
  addValueCaseInsensitive,
  missingCombinations,
  previewVariantSku,
} from '@/pages/product-variant-payload';

/**
 * Attribute builder + generated variant grid, shared by the New Product
 * wizard and the product page's "Add variants" modal.
 *
 * Attributes come from the tenant library (chips) or are typed fresh; values
 * pre-fill from the library as toggle chips and accept free text. Nothing is
 * written to the library from here — new names/values are persisted by
 * `POST /products/:id/variants`, which canonicalises them.
 *
 * Locked mode (`lockedAttributeNames`): the parent already has variants, so
 * attribute names are fixed and only values can be added; Generate builds just
 * the combinations not already in `existingCombinationKeys`.
 */
export interface VariantMatrixBuilderProps {
  attributes: AttributeDef[];
  onAttributesChange: (next: AttributeDef[]) => void;
  variants: VariantRow[];
  onVariantsChange: (next: VariantRow[]) => void;
  library: VariantAttribute[];
  lockedAttributeNames?: string[];
  /** normalizedCombinationKey()s of the parent's existing children. */
  existingCombinationKeys?: string[];
  /** Used to preview the SKU a blank field will get. */
  parentSku?: string;
  /** paise */
  parentPrice: number;
  /** paise */
  parentCost: number;
  currency: CurrencyCode;
  error?: string;
}

const CHIP_ON =
  'bg-violet-600 text-white shadow-sm hover:bg-violet-700 dark:bg-violet-500 dark:hover:bg-violet-400';
const CHIP_OFF =
  'bg-white text-violet-700 ring-1 ring-inset ring-violet-200 hover:bg-violet-50 dark:bg-neutral-800 dark:text-violet-300 dark:ring-violet-800 dark:hover:bg-violet-900/30';

/** "Size: M / Colour: Red" from a normalized key — for the read-only existing rows. */
function describeKey(key: string): string {
  return key
    .split('|')
    .map((part) => part.split('=').slice(1).join('='))
    .join(' / ');
}

export function VariantMatrixBuilder({
  attributes,
  onAttributesChange,
  variants,
  onVariantsChange,
  library,
  lockedAttributeNames,
  existingCombinationKeys = [],
  parentSku = '',
  parentPrice,
  parentCost,
  currency,
  error,
}: VariantMatrixBuilderProps) {
  const locked = (lockedAttributeNames?.length ?? 0) > 0;
  const symbol = getCurrencySymbol(currency);
  const seq = useRef(0);
  const nextId = () => `attr-${Date.now().toString(36)}-${seq.current++}`;

  // Text currently typed into each attribute's "add value" box, keyed by id.
  const [valueDrafts, setValueDrafts] = useState<Record<string, string>>({});
  const [newAttributeDraft, setNewAttributeDraft] = useState('');

  const hasAttribute = (name: string) =>
    attributes.some((a) => a.name.trim().toLowerCase() === name.trim().toLowerCase());

  const libraryChips = library.filter((a) => a.is_active && !hasAttribute(a.name));

  function addAttribute(name: string) {
    const typed = name.trim();
    if (!typed || hasAttribute(typed)) return;
    // A typed name that matches the library reuses its canonical spelling + values.
    const lib = findLibraryAttribute(library, typed);
    onAttributesChange([
      ...attributes,
      { id: nextId(), name: lib?.name ?? typed, values: lib ? libraryValuesFor(library, lib.name) : [] },
    ]);
    setNewAttributeDraft('');
  }

  function updateAttribute(id: string, patch: Partial<AttributeDef>) {
    onAttributesChange(attributes.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }

  function removeAttribute(id: string) {
    onAttributesChange(attributes.filter((a) => a.id !== id));
  }

  function toggleValue(attr: AttributeDef, value: string) {
    const on = attr.values.some((v) => v.toLowerCase() === value.toLowerCase());
    updateAttribute(attr.id, {
      values: on
        ? attr.values.filter((v) => v.toLowerCase() !== value.toLowerCase())
        : addValueCaseInsensitive(attr.values, value, libraryValuesFor(library, attr.name)),
    });
  }

  function addDraftValue(attr: AttributeDef) {
    const draft = valueDrafts[attr.id] ?? '';
    if (!draft.trim()) return;
    updateAttribute(attr.id, {
      values: addValueCaseInsensitive(attr.values, draft, libraryValuesFor(library, attr.name)),
    });
    setValueDrafts((d) => ({ ...d, [attr.id]: '' }));
  }

  // Combinations that Generate will produce — everything, or only the missing
  // ones when the parent already has variants.
  const pendingCombos = locked
    ? missingCombinations(attributes, existingCombinationKeys)
    : generateCombinations(attributes);

  function handleGenerate() {
    onVariantsChange(buildRowsFromCombinations(pendingCombos, variants));
  }

  function updateRow(id: string, patch: Partial<VariantRow>) {
    onVariantsChange(variants.map((v) => (v.id === id ? { ...v, ...patch } : v)));
  }

  const pricePlaceholder = paiseToRupeeInput(parentPrice, currency) || '0';
  const costPlaceholder = paiseToRupeeInput(parentCost, currency) || '0';

  return (
    <div className="space-y-4 animate-fade-in">
      {error && (
        <div data-testid="variant-matrix-error">
          <Alert variant="danger" title={error} />
        </div>
      )}

      {/* ── Attribute builder ── */}
      <div className="space-y-3">
        {attributes.map((attr) => {
          const libValues = libraryValuesFor(library, attr.name);
          // Library values first (in library order), then anything typed here.
          const chips = [
            ...libValues,
            ...attr.values.filter((v) => !libValues.some((lv) => lv.toLowerCase() === v.toLowerCase())),
          ];
          const isOn = (v: string) => attr.values.some((x) => x.toLowerCase() === v.toLowerCase());
          const isLibrary = (v: string) => libValues.some((lv) => lv.toLowerCase() === v.toLowerCase());

          return (
            <div
              key={attr.id}
              className="rounded-xl border border-violet-100 bg-gradient-to-br from-violet-50/60 to-pink-50/40 p-4 space-y-3 transition-all duration-200 hover:shadow-md dark:border-violet-900/40 dark:from-violet-900/15 dark:to-pink-900/10"
            >
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-300 mb-1" htmlFor={`${attr.id}-name`}>
                    Attribute
                  </label>
                  {locked ? (
                    <p
                      id={`${attr.id}-name`}
                      className="rounded-lg bg-white/70 px-3 py-2 text-sm font-semibold text-violet-800 dark:bg-neutral-800/60 dark:text-violet-200"
                    >
                      {attr.name}
                    </p>
                  ) : (
                    <Input
                      id={`${attr.id}-name`}
                      placeholder="e.g., Size, Colour, Gauge"
                      value={attr.name}
                      onChange={(val) => updateAttribute(attr.id, { name: typeof val === 'string' ? val : '' })}
                    />
                  )}
                </div>
                {!locked && (
                  <button
                    type="button"
                    onClick={() => removeAttribute(attr.id)}
                    className="mb-2 text-xs font-medium text-rose-600 hover:text-rose-700 dark:text-rose-400 transition-colors duration-200"
                    aria-label={`Remove attribute ${attr.name || 'unnamed'}`}
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {chips.map((value) => (
                  <button
                    key={`${attr.id}-v-${value.toLowerCase()}`}
                    type="button"
                    onClick={() => toggleValue(attr, value)}
                    aria-pressed={isOn(value)}
                    aria-label={`${isOn(value) ? 'Exclude' : 'Include'} value ${value}`}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium transition-all duration-200',
                      isOn(value) ? CHIP_ON : CHIP_OFF,
                    )}
                  >
                    {value}
                    {isOn(value) && <span aria-hidden="true" className="text-xs opacity-80">{isLibrary(value) ? '✓' : '×'}</span>}
                  </button>
                ))}
                <Input
                  id={`${attr.id}-newvalue`}
                  placeholder="Add value, press Enter"
                  value={valueDrafts[attr.id] ?? ''}
                  onChange={(val) => setValueDrafts((d) => ({ ...d, [attr.id]: typeof val === 'string' ? val : '' }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addDraftValue(attr);
                    }
                  }}
                  className="w-44"
                />
                <Button variant="ghost" size="sm" onClick={() => addDraftValue(attr)}>
                  + Add Value
                </Button>
              </div>
            </div>
          );
        })}

        {!locked && (
          <div className="space-y-2">
            {libraryChips.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                  From library
                </span>
                {libraryChips.map((lib) => (
                  <button
                    key={lib.id}
                    type="button"
                    onClick={() => addAttribute(lib.name)}
                    className={cn('inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium transition-all duration-200', CHIP_OFF)}
                    aria-label={`Add attribute ${lib.name} from library`}
                  >
                    + {lib.name}
                    {lib.values.some((v) => v.is_active) && (
                      <span className="text-xs opacity-70">({lib.values.filter((v) => v.is_active).length})</span>
                    )}
                  </button>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Input
                id="variant-new-attribute"
                placeholder="New attribute (e.g., Material)"
                value={newAttributeDraft}
                onChange={(val) => setNewAttributeDraft(typeof val === 'string' ? val : '')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAttribute(newAttributeDraft);
                  }
                }}
                className="w-56"
              />
              <Button variant="secondary" onClick={() => addAttribute(newAttributeDraft)} disabled={!newAttributeDraft.trim()}>
                + Add Attribute
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          {usableAttributes(attributes).length > 0 && (
            <Button
              variant="primary"
              onClick={handleGenerate}
              disabled={pendingCombos.length === 0}
              data-testid="preview-variant-matrix"
            >
              Generate {pendingCombos.length} {pendingCombos.length === 1 ? 'Variant' : 'Variants'}
            </Button>
          )}
          <span className="text-xs text-neutral-500 dark:text-neutral-400" data-testid="variant-matrix-count">
            {variants.length} combinations
          </span>
          {locked && pendingCombos.length === 0 && usableAttributes(attributes).length > 0 && (
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              Every combination already exists — add a value to create more.
            </span>
          )}
        </div>
      </div>

      {/* ── Generated combinations ── */}
      {variants.length > 0 && (
        <div className="mt-2 overflow-x-auto rounded-xl border border-neutral-200 dark:border-neutral-700" data-testid="variant-matrix-preview">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-800">
              <tr className="border-b border-neutral-200 dark:border-neutral-700">
                <th className="px-2 py-2 text-left font-medium text-neutral-500">On</th>
                <th className="px-2 py-2 text-left font-medium text-neutral-500">Variant</th>
                <th className="px-2 py-2 text-left font-medium text-neutral-500">SKU</th>
                <th className="px-2 py-2 text-left font-medium text-neutral-500">Barcode</th>
                <th className="px-2 py-2 text-left font-medium text-neutral-500">Price ({symbol})</th>
                <th className="px-2 py-2 text-left font-medium text-neutral-500">Cost ({symbol})</th>
                <th className="px-2 py-2 text-left font-medium text-neutral-500">Stock</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((v) => {
                const attrs = buildVariantAttributes(v);
                const label = Object.values(attrs).join(' / ');
                const sku = v.sku.trim() || previewVariantSku(parentSku, attrs);
                return (
                  <tr
                    key={v.id}
                    data-testid={`variant-row-${sku}`}
                    className={cn(
                      'border-b border-neutral-100 even:bg-neutral-50 hover:bg-brand-50 transition-colors duration-200 animate-slide-up dark:border-neutral-800 dark:even:bg-neutral-800/40 dark:hover:bg-neutral-800',
                      !v.enabled && 'opacity-40',
                    )}
                  >
                    <td className="px-2 py-2">
                      <input
                        type="checkbox"
                        checked={v.enabled}
                        onChange={(ev) => updateRow(v.id, { enabled: ev.target.checked })}
                        aria-label={`Include variant ${label}`}
                        className="h-4 w-4 accent-violet-600"
                      />
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap font-medium text-neutral-900 dark:text-neutral-100">{label}</td>
                    <td className="px-2 py-2">
                      <Input
                        id={`${v.id}-sku`}
                        placeholder="Auto"
                        value={v.sku}
                        onChange={(val) => updateRow(v.id, { sku: typeof val === 'string' ? val : '' })}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        id={`${v.id}-barcode`}
                        placeholder="Optional"
                        value={v.barcode}
                        onChange={(val) => updateRow(v.id, { barcode: typeof val === 'string' ? val : '' })}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        id={`${v.id}-price`}
                        type="number"
                        variant="currency"
                        currency={currency}
                        placeholder={pricePlaceholder}
                        value={v.price}
                        onChange={(val) => updateRow(v.id, { price: typeof val === 'string' ? val : '' })}
                        min={0}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        id={`${v.id}-cost`}
                        type="number"
                        variant="currency"
                        currency={currency}
                        placeholder={costPlaceholder}
                        value={v.cost}
                        onChange={(val) => updateRow(v.id, { cost: typeof val === 'string' ? val : '' })}
                        min={0}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <Input
                        id={`${v.id}-stock`}
                        type="number"
                        placeholder="0"
                        value={v.stock}
                        onChange={(val) => updateRow(v.id, { stock: typeof val === 'string' ? val : '' })}
                        min={0}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="px-3 py-2 text-xs text-neutral-500 dark:text-neutral-400">
            Blank SKU is generated automatically; blank price / cost inherit the parent's.
          </p>
        </div>
      )}

      {/* ── Existing combinations (locked mode) ── */}
      {locked && existingCombinationKeys.length > 0 && (
        <div className="rounded-xl bg-neutral-50/70 p-3 dark:bg-neutral-800/40">
          <p className="mb-2 text-xs font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            Already exists ({existingCombinationKeys.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {existingCombinationKeys.map((key) => (
              <span
                key={key}
                data-testid="variant-existing-row"
                className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-800"
              >
                {describeKey(key)}
                <span aria-hidden="true">✓</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
